import React, { useState, useEffect, useRef } from 'react';
import withInfiniteScroll from 'global/components/withInfiniteScroll';
import useRetrieveData from 'global/hooks/useRetrieveData';
import { useSelector, useDispatch } from 'react-redux';
import { buildFilters } from 'global/utils/filter';
import { getFirstKey } from 'global/utils';
import { get, set, size } from 'global/lib/lodash';
import { List, Icon, Menu, MenuItem, ListSubheader, ListItemIcon, ListItemText, Box } from '@mui/material';
import TaskListItem from './TaskListItem';
import { RowFlex, CreateButton } from './TaskList.styles';
import TaskCreate from '../TaskCreate';
import { useHistory } from 'react-router-dom';
import setFilter from 'redux/actions/setFilter';
import { userHasPermissions } from 'redux/actions/userLogin';
import { Access } from 'global/components/droprealms';
import Modal from 'global/components/modal';
import Styles from 'global/styles';
import sendData from 'redux/actions/sendData';
import { setTaskList } from 'redux/actions/view';
import { withRouter } from 'react-router-dom';
import setUserRealm from 'redux/actions/userRealm';
import withSearchBar from 'global/components/withSearchBar';
import { Text } from 'global/components/text';
import PerformTasksFilters from '../Filters';
import { useScreen } from 'global/styles';
import { FilterChips } from 'global/components/filterchips';
import { wait } from '../../../global/utils';
import { useTaskTypes } from '../task-type-utils/useTaskTypes';
import { getAvailableTaskTypes, getAvailableTaskTypesForTier, getTaskTypeIcon, groupTaskTypesByMode } from '../task-type-utils';

const Includes = ['realm', 'realm.parent'];
const pageSize = 25;

export default withRouter((props) => {
  const include = Includes.join(',');
  const filter = useSelector((state) => get(state, 'filter.tasks.list'));
  const hasRealm = useSelector((state) => get(state, 'auth.realm'));
  const [hasChangedFilter, setHasChangedFilter] = useState(false);
  const dispatch = useDispatch();
  const styles = Styles();

  const { isMobile } = useScreen();

  const filterProps = () => {
    return buildFilters(filter, {
      includeRealm: true,
      convertName: { searchstring: 'name', realm_type: 'tier' },
      dontFilter: ['content'],
      overrideFilter: (_filtObj) => {
        const filtObj = { ..._filtObj };
        if (filtObj.status) {
          set(filtObj, filtObj.status, true);
          delete filtObj.status;
        }
        if (filtObj.vendor_id === 0) {
          delete filtObj.vendor_id;
        }
        delete filtObj.age;
        return filtObj;
      },
      overrideSort: (sort) => {
        if (sort.indexOf('name') > -1) {
          return ['name'];
        }
        if (sort.indexOf('count') > -1) {
          return ['-total'];
        }
        return sort;
      },
    });
  };

  const [urlParams, setUrlParams] = useState({ build: ['processedTaskCountValues'], waitFor: null, params: { ...filterProps(), page: { size: pageSize, number: 1 }, include } });
  const [realmDialog, setRealmDialog] = useState({
    open: false,
    title: 'Choose Location',
    onClose: () => setRealmDialog((d) => ({ ...d, open: false })),
  });
  const [createTask, setCreateTask] = useState({ open: false });
  const [typeMenuAnchor, setTypeMenuAnchor] = useState(null);
  const selectedTaskTypeRef = useRef(null);
  // After creating a task from bare /tasks/list, always navigate to that location's
  // processed-task-count page — even when the filtered list has multiple rows.
  const pendingRedirectRealmId = useRef(null);
  const { data: taskTypesData } = useTaskTypes();
  const realmTier = get(hasRealm, 'tier');
  const availableTaskTypes = getAvailableTaskTypesForTier(getAvailableTaskTypes(taskTypesData), realmTier);

  const selectedTaskType = selectedTaskTypeRef.current;
  const locationTiers = size(get(selectedTaskType, 'location_types'))
    ? selectedTaskType.location_types
    : ['division', 'community', 'lot'];

  const history = useHistory();

  const findProcessedTaskCountId = (items, realmId, { allowFirst = false } = {}) => {
    const list = Array.isArray(items) ? items : [];
    const match = list.find((item) => String(get(item, 'realm.id')) === String(realmId));
    if (get(match, 'id')) return get(match, 'id');
    // realmid-filtered API responses are scoped to the target location; the list view is not.
    return allowFirst ? get(list, '0.id') : undefined;
  };

  const redirectToProcessedTaskCount = (id) => {
    if (!id) return false;
    pendingRedirectRealmId.current = null;
    history.push(`/tasks/list/${id}`);
    return true;
  };

  useEffect(() => {
    if (!hasChangedFilter && filter.searchstring) {
      setHasChangedFilter(true);
      dispatch(setFilter('tasks', 'list', set({ ...filter }, '_sort.sortBy', 'name')));
    }
  }, [filter.searchstring]);

  useEffect(() => {
    setUrlParams({
      ...urlParams,
      useEffect: true,
      waitFor: undefined,
      params: {
        ...urlParams.params,
        ...{ page: { size: pageSize, number: 1 }, include },
        ...filterProps(),
      },
    });
  }, [filter, hasRealm]);

  const tasks = useRetrieveData({ url: '/processed-task-count-values' }, urlParams);

  const openCreateTask = (realm, taskType) => {
    if (realm) {
      dispatch(setUserRealm(realm));
      setCreateTask({
        open: true,
        taskType: taskType || null,
        realmItem: realm
          ? {
              realm,
              realmName: get(realm, 'name', ''),
            }
          : null,
        onClose: async (task) => {
          if (task && realm) {
            pendingRedirectRealmId.current = realm.id;
            setCreateTask({ open: false });
            selectedTaskTypeRef.current = null;
            // Backend can lag creating the processed-task-count row; retry briefly.
            for (let attempt = 0; attempt < 5; attempt++) {
              await wait(attempt === 0 ? 500 : 1000);
              if (!pendingRedirectRealmId.current) return;
              const da = await sendData(
                { url: '/processed-task-count-values' },
                {
                  normalize: { build: ['processedTaskCountValues'] },
                  params: { filter: { realmid: realm.id }, include },
                }
              );
              if (redirectToProcessedTaskCount(findProcessedTaskCountId(da, realm.id, { allowFirst: true }))) {
                return;
              }
            }
          } else {
            setCreateTask({ open: false });
            selectedTaskTypeRef.current = null;
          }
        },
      });
    }
  };

  const closeDialog = (realm) => {
    openCreateTask(realm, selectedTaskTypeRef.current);
    setRealmDialog((d) => ({ ...d, open: false }));
  };

  useEffect(() => {
    dispatch(setTaskList(tasks.data));
    if (get(createTask, 'open')) return;

    const pendingRealmId = pendingRedirectRealmId.current;
    if (pendingRealmId) {
      const id = findProcessedTaskCountId(tasks.data, pendingRealmId);
      if (redirectToProcessedTaskCount(id)) return;
    }

    // Auto-drill into a single result only for Lots — other tiers can legitimately
    // show one row without meaning the user wants to leave the location list.
    if (
      size(tasks.data) === 1 &&
      get(hasRealm, 'tier') === 'lot' &&
      get(tasks, 'data.0.realm.id') === get(hasRealm, 'id')
    ) {
      props.history.push(`/tasks/list/${get(tasks, 'data.0.id')}`);
    }
  }, [tasks.data]);

  const proceedToRealm = (taskType) => {
    if (hasRealm && hasRealm.tier === 'lot') {
      return openCreateTask(hasRealm, taskType);
    }
    setRealmDialog((d) => ({ ...d, open: true }));
  };

  const openRealm = (e) => {
    if (size(availableTaskTypes) > 1) {
      setTypeMenuAnchor(e.currentTarget);
    } else if (size(availableTaskTypes) === 1) {
      selectedTaskTypeRef.current = availableTaskTypes[0];
      proceedToRealm(availableTaskTypes[0]);
    } else {
      proceedToRealm(null);
    }
  };

  const lastPage = get(tasks.meta, 'page.last-page', null);
  const updatePage = (newPage) => {
    if (newPage <= lastPage) {
      setUrlParams({
        ...urlParams,
        params: {
          ...urlParams.params,
          page: { size: pageSize, number: newPage },
        },
      });
    }
  };

  return (
    <>
      <RowFlex>
        <Box sx={{ pl: 1 }}>
          {!isMobile && <Text type={'headline_medium'}>
            Tasks by Location
          </Text>}
          {isMobile ? <PerformTasksFilters {...props} /> : <FilterChips id={'tasks.list'} />}
        </Box>
        <CreateButton data-testid={"tasks-location-task-create-button"} disabled={!userHasPermissions('createTasks')} variant="contained" color="secondary" onClick={openRealm} endIcon={size(availableTaskTypes) > 1 ? <Icon>arrow_drop_down</Icon> : undefined}>
          {size(availableTaskTypes) === 1 ? `ADD ${(availableTaskTypes[0].name || '').toUpperCase()}` : 'CREATE NEW'}
        </CreateButton>
        <Menu
          anchorEl={typeMenuAnchor}
          open={Boolean(typeMenuAnchor)}
          onClose={() => setTypeMenuAnchor(null)}
        >
          {(() => {
            const typeGroups = groupTaskTypesByMode(availableTaskTypes);
            const renderItem = (tt) => (
              <MenuItem
                data-testid={`tasks-location-task-create-button-menu-${tt.id}`}
                key={tt.id}
                onClick={() => {
                  selectedTaskTypeRef.current = tt;
                  setTypeMenuAnchor(null);
                  proceedToRealm(tt);
                }}
              >
                <ListItemIcon sx={{ minWidth: 32 }}>
                  <Icon fontSize="small">{getTaskTypeIcon(tt)}</Icon>
                </ListItemIcon>
                <ListItemText>{(tt.name || '').toUpperCase()}</ListItemText>
              </MenuItem>
            );
            if (typeGroups.grouped) {
              return typeGroups.groups.flatMap((group) => [
                <ListSubheader key={group.label} sx={{ lineHeight: '32px', fontSize: 12 }}>
                  {group.label}
                </ListSubheader>,
                ...group.items.map(renderItem),
              ]);
            }
            return availableTaskTypes.map(renderItem);
          })()}
        </Menu>
      </RowFlex>

      <ListWithPages
        handlePageChange={(np) => updatePage(np)}
        currentPage={get(urlParams, 'params.page.number', 1)}
        lastPage={lastPage}
        data={tasks.data}
        loading={tasks.loading}
        header={null}
        {...props}
      />
      <Modal {...realmDialog}>
        <div className={styles.formContainer}>
          <Access
            openToSelected={true}
            onlyTiers={locationTiers}
            currentRealm={hasRealm}
            canAdd={true}
            singleSelect={true}
            disableListScroll
            open={!!realmDialog.open}
            onSelectedChange={(s) => closeDialog(getFirstKey(s))}
            selectTitle="Choose a location for the Task"
          />
        </div>
      </Modal>
      <TaskCreate {...createTask} />
    </>
  );
});

const ListWithPages = withSearchBar(withInfiniteScroll(TaskListItem, List));
